import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Sparkles } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { SectionHead } from "@/components/landing/Section";
import { BUSINESS_CATS } from "@/config/business";

const AreaBanner = ({ cat, flip }) => {
  const { t } = useLang();
  const b = t.pages.business;
  const c = b.cats[cat.key];
  return (
    <motion.article
      id={`area-${cat.key}`}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.5 }}
      data-testid={`business-area-${cat.key}`}
      className="scroll-mt-28 journal-card overflow-hidden grid md:grid-cols-2"
    >
      <div className={`relative min-h-[240px] md:min-h-[380px] ${flip ? "md:order-2" : ""}`}>
        <img src={`/business/${cat.key}.webp`} alt={c.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover" />
      </div>
      <div className="p-7 md:p-10 flex flex-col">
        <div className="text-xs font-extrabold uppercase tracking-[0.16em] text-amber-dark">{b.bannerKicker}</div>
        <h3 className="mt-3 font-display text-2xl md:text-3xl font-semibold tracking-tight text-ink">{c.name}</h3>
        <p className="mt-2 font-display italic text-lg text-rowan">{c.short}</p>
        <p className="mt-4 text-ink-muted leading-relaxed">{c.lead}</p>
        <div className="mt-5 text-xs font-extrabold uppercase tracking-[0.14em] text-ink-muted">{b.placesLike}</div>
        <ul className="mt-2 flex flex-wrap gap-2">
          {c.types.map((ty) => (
            <li key={ty} className="rounded-full border border-line bg-parchment px-3 py-1 text-sm font-semibold text-ink">{ty}</li>
          ))}
        </ul>
        <p className="mt-5 flex gap-2 text-sm text-ink">
          <Sparkles className="w-4 h-4 mt-0.5 shrink-0 text-amber" />
          <span><strong>{b.ideaTitle}:</strong> {c.idea}</span>
        </p>
        <div className="mt-auto pt-7">
          <p className="font-display text-lg font-semibold text-ink">{b.bannerTitle}</p>
          <Link to={{ search: `?role=${cat.key}`, hash: "#signup" }} data-testid={`business-area-${cat.key}-cta`} className="mt-3 inline-flex items-center gap-2 rounded-full bg-moss hover:bg-moss-dark text-parchment px-6 py-3 font-extrabold transition-[background-color,transform] duration-200 hover:-translate-y-0.5">
            {b.cta} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </motion.article>
  );
};

export default function BusinessAreas() {
  const { t } = useLang();
  const b = t.pages.business;
  return (
    <section id="areas" data-testid="business-areas" className="scroll-mt-28">
      <SectionHead kicker={b.areasKicker} title={b.areasTitle} body={b.lead} />
      <nav aria-label={b.areasKicker} className="mt-8 flex flex-wrap gap-2">
        {BUSINESS_CATS.map((c) => (
          <a key={c.key} href={`#area-${c.key}`} data-testid={`business-area-chip-${c.key}`} className="rounded-full border border-line bg-paper px-4 py-2 text-sm font-bold text-ink hover:border-moss hover:text-moss transition-colors">
            {b.cats[c.key].name}
          </a>
        ))}
      </nav>
      <div className="mt-12 space-y-8">
        {BUSINESS_CATS.map((cat, i) => <AreaBanner key={cat.key} cat={cat} flip={i % 2 === 1} />)}
      </div>
    </section>
  );
}
